/* eslint-disable camelcase */
import * as React from 'react'
import { t } from 'i18next'
import {
  InputHTMLAttributes,
  TextareaHTMLAttributes,
  SelectHTMLAttributes,
  OptionHTMLAttributes,
  LabelHTMLAttributes,
  FieldsetHTMLAttributes
} from 'react'
import { sluggify, toggleLabelFocus } from './helpers'

export type FormStyle = Partial<{
  block_classname: string
  inview_classname: string
  inner_classname: string
  header_classname: string
  title_classname: string
  intro_classname: string
  content_classname: string
  subtitle_classname: string
  text_classname: string
  form_classname: string
  fieldset_classname: string
  fieldset_inner_classname: string
  fieldset_legend_classname: string
  fieldset_error_classname: string
  footer_classname: string
  visual_classname: string
  image_classname: string
  clickable_wrapper_classname: string
  clickable_primary_classname: string
  clickable_secondary_classname: string
}>

type InputProps = {
  kind: 'input'
  extraClasses?: string
  htmlAttributes: { required: boolean } & InputHTMLAttributes<HTMLInputElement>
}

export const Input = (props: InputProps): JSX.Element => (
  <input
    className={`form__input ${props.extraClasses ? props.extraClasses : ''}`}
    type={props.htmlAttributes.type ? props.htmlAttributes.type : 'text'}
    aria-required={props.htmlAttributes.required ? true : false}
    {...props.htmlAttributes}
  />
)

type BooleanProps = {
  kind: 'boolean'
  extraClasses?: string
  htmlAttributes: { required: boolean; type: 'checkbox' | 'radio' } & InputHTMLAttributes<HTMLInputElement>
}

export const Boolean = (props: BooleanProps): JSX.Element => (
  <input
    className={`form__boolean form__${props.htmlAttributes.type} ${props.extraClasses ? props.extraClasses : ''}`}
    type={props.htmlAttributes.type}
    aria-required={props.htmlAttributes.required ? true : false}
    {...props.htmlAttributes}
  />
)

type TextAreaProps = {
  kind: 'textArea'
  extraClasses?: string
  htmlAttributes: { required: boolean } & TextareaHTMLAttributes<HTMLTextAreaElement>
}

export const TextArea = (props: TextAreaProps): JSX.Element => (
  <textarea
    className={`form__textarea ${props.extraClasses ? props.extraClasses : ''}`}
    aria-required={props.htmlAttributes.required ? true : false}
    {...props.htmlAttributes}
  />
)

type SelectProps = {
  kind: 'select'
  extraClasses?: string
  options: { text: string; htmlAttributes?: OptionHTMLAttributes<HTMLOptionElement> }[]
  htmlAttributes: { required: boolean; id: string } & SelectHTMLAttributes<HTMLSelectElement>
}

export const Select = (props: SelectProps): JSX.Element => (
  <div
    className={`form__select ${props.extraClasses ? props.extraClasses : ''} ${
      props.htmlAttributes.disabled ? 'form__select--disabled' : ''
    }`}
  >
    <select
      onFocus={(e) => toggleLabelFocus(props.htmlAttributes.id)}
      onBlur={(e) => toggleLabelFocus(props.htmlAttributes.id)}
      aria-required={props.htmlAttributes.required ? true : false}
      {...props.htmlAttributes}
    >
      {props.options &&
        props.options.map((option) => (
          <option
            key={
              option.htmlAttributes && option.htmlAttributes.id ? option.htmlAttributes.id : `${sluggify(option.text)}`
            }
            {...option.htmlAttributes}
          >
            {option.text}
          </option>
        ))}
    </select>
  </div>
)

type LabelProps = {
  extraClasses?: string
  label: string
  htmlAttributes: { htmlFor: string } & LabelHTMLAttributes<HTMLLabelElement>
}

export const Label = (props: LabelProps): JSX.Element => (
  <label
    className={`form__label ${props.extraClasses ? props.extraClasses : ''}`}
    dangerouslySetInnerHTML={{ __html: props.label }}
    {...props.htmlAttributes}
  />
)

type FieldSetProps = {
  extraClasses?: string
  legend: string
  alignRow: boolean
  children: React.ReactNode
  fieldSetElementProps?: FieldsetHTMLAttributes<HTMLFieldSetElement>
}

export const FieldSetLegend = (props: FieldSetProps): JSX.Element => (
  <fieldset
    className={`form__fieldset ${props.extraClasses ? props.extraClasses : ''}`}
    {...props.fieldSetElementProps}
  >
    <legend className="form__legend">{props.legend}</legend>
    {props.alignRow ? <div className="form__fieldset-inner">{props.children}</div> : props.children}
  </fieldset>
)

type FormGroupProps = {
  reversed: boolean
  extraClasses?: string
  children: React.ReactNode
}

export const FormGroup = (props: FormGroupProps): JSX.Element => (
  <div
    className={`form__group ${props.reversed ? 'form__group--reverse' : ''}
  ${props.extraClasses ? props.extraClasses : ''}`}
  >
    {props.children}
  </div>
)

type ErrorMessageProps = {
  id: string
  message: string
}

export const ErrorMessage = (props: ErrorMessageProps): JSX.Element => (
  <p id={props.id} className="form__description form__description--error form__description--input" role="alert">
    {props.message}
  </p>
)

type DescriptionProps = {
  id: string
  message: string
}

export const Description = (props: DescriptionProps): JSX.Element => (
  <p id={props.id} className="form__description form__description--input">
    {props.message}
  </p>
)

type ElementProps = InputProps | TextAreaProps | SelectProps | BooleanProps

type FormElementProps = {
  elementProps: ElementProps
  labelProps: LabelProps
  descriptionProps?: DescriptionProps
  errorMessageProps?: ErrorMessageProps
  formGroupExtraClasses?: string
}

export const FormElement = ({
  elementProps,
  labelProps,
  descriptionProps,
  errorMessageProps,
  formGroupExtraClasses
}: FormElementProps): JSX.Element => {
  const error = errorMessageProps && errorMessageProps.message

  return (
    <FormGroup
      reversed={!(elementProps.kind == 'boolean')}
      extraClasses={`
        ${elementProps.kind == 'boolean' ? `form__group--${elementProps.htmlAttributes.type}` : ''} 
        ${error ? 'form__group--error' : ''} 
        ${formGroupExtraClasses ? formGroupExtraClasses : ''}
      `}
    >
      {elementProps.kind == 'input' ? (
        <Input {...elementProps} />
      ) : elementProps.kind == 'textArea' ? (
        <TextArea {...elementProps} />
      ) : elementProps.kind == 'select' ? (
        <Select {...elementProps} />
      ) : (
        <Boolean {...elementProps} />
      )}
      <Label
        extraClasses={elementProps.kind == 'boolean' ? 'text--styled' : ''}
        {...labelProps}
        label={`${
          labelProps.label + (elementProps.htmlAttributes.required === false ? ' ' + t('Form:Fields.Optional') : '')
        }`}
      />

      {descriptionProps && descriptionProps.message && <Description {...descriptionProps} />}
      {error && <ErrorMessage {...errorMessageProps} />}
    </FormGroup>
  )
}

export const headers = () => {
  const headers = new Headers()
  headers.append('content-type', 'application/json')
  headers.append(
    'X-XSRF-TOKEN',
    (document.getElementsByName('__RequestVerificationToken')[0] as HTMLInputElement).value
  )
  return headers
}
